import { closeHistory, history, redo, redoDepth, undo, undoDepth } from 'prosemirror-history';
import { EditorState, type Plugin, type Transaction } from 'prosemirror-state';
import type { Node as ProseMirrorNode } from 'prosemirror-model';
import type { EditorView } from 'prosemirror-view';
import { Mapping } from 'prosemirror-transform';

/** One authoritative state/history. Views are projections, never independent content stores. */
export class DocumentSession {
  state: EditorState;
  private readonly views = new Set<EditorView>();
  private readonly listeners = new Set<(state: EditorState, transaction?: Transaction) => void>();
  constructor(doc: ProseMirrorNode, private readonly plugins: readonly Plugin[] = []) {
    this.state = this.createState(doc);
  }
  private createState(doc: ProseMirrorNode) { return EditorState.create({ doc, schema: doc.type.schema, plugins: [history({ depth: 100 }), ...this.plugins] }); }
  get canUndo() { return undoDepth(this.state) > 0; }
  get canRedo() { return redoDepth(this.state) > 0; }
  subscribe(listener: (state: EditorState, transaction?: Transaction) => void) {
    this.listeners.add(listener); return () => { this.listeners.delete(listener); };
  }
  attach(view: EditorView) { this.views.add(view); this.updateView(view); return () => { this.views.delete(view); }; }
  private updateView(view: EditorView, mapping?: Mapping, source?: EditorView) {
    let projected = this.state;
    if (view !== source && view.state) {
      try {
        const bookmark = view.state.selection.getBookmark();
        const selection = (mapping ? bookmark.map(mapping) : bookmark).resolve(this.state.doc);
        projected = this.state.apply(this.state.tr.setSelection(selection).setMeta('addToHistory', false));
      } catch { /* A deleted selection falls back to the document's valid selection. */ }
    }
    view.updateState(projected);
  }
  /** Wire a preview's dispatchTransaction here to keep that preview's selection local. */
  dispatchFrom(view: EditorView, transaction: Transaction) { this.dispatch(transaction, view); }
  dispatch = (transaction: Transaction, source?: EditorView) => {
    const result = this.state.applyTransaction(transaction);
    this.state = result.state;
    const mapping = new Mapping();
    for (const applied of result.transactions) mapping.appendMapping(applied.mapping);
    for (const view of this.views) this.updateView(view, mapping, source);
    for (const listener of this.listeners) listener(this.state, transaction);
  };
  replace(doc: ProseMirrorNode, options: { history?: boolean; separateEvent?: boolean } = {}) {
    const previous = this.state.doc;
    const start = previous.content.findDiffStart(doc.content);
    if (start === null) return false;
    const end = previous.content.findDiffEnd(doc.content)!;
    const overlap = start - Math.min(end.a, end.b);
    const fromEnd = end.a + Math.max(0, overlap);
    const toEnd = end.b + Math.max(0, overlap);
    let tr = this.state.tr.replace(start, fromEnd, doc.slice(start, toEnd));
    if (options.separateEvent !== false) tr = closeHistory(tr);
    if (options.history === false) tr.setMeta('addToHistory', false);
    this.dispatch(tr);
    return true;
  }
  reset(doc: ProseMirrorNode) {
    // Keep review/plugin state across version loads, but start a fresh content history.
    this.state = this.state.apply(this.state.tr.replaceWith(0, this.state.doc.content.size, doc.content).setMeta('addToHistory', false));
    this.state = this.state.reconfigure({ plugins: [...this.plugins] }).reconfigure({ plugins: [history({ depth: 100 }), ...this.plugins] });
    for (const view of this.views) this.updateView(view);
    for (const listener of this.listeners) listener(this.state);
  }
  undo() { return undo(this.state, this.dispatch); }
  redo() { return redo(this.state, this.dispatch); }
}
